import { Box, LinearProgress } from '@mui/material';
import DisciplineStats from 'components/DisciplineStats';
import { useState, useEffect } from 'react';
import { useMember, useSession } from 'components/Providers';
import { useDispatch, useSelector } from 'react-redux';
import * as action from 'store/member/discipline-stats';
import { getMarkingPeriods } from 'store/lookup';

const ProfileDisciplineStats = () => {
    const dispatch = useDispatch();
    const member = useMember();
    const disciplineItems = useSelector(state => state.member.list?.find(item => item.id === member.id)).discipline;
    const disciplineLoading = useSelector(state => state.member.disciplineLoading);
    const currentMarkingPeriodId = useSelector(state => state.lookup.currentMarkingPeriodId);
    const { academicYear } = useSession();

    const [data, setData] = useState({
        stats: { totalPoints: 0, merits: 0, demerits: 0 },
        weekData: [],
        incidents: []
    });

    useEffect(() => {
        if (currentMarkingPeriodId) {
            setData({
                stats: { totalPoints: 0, merits: 0, demerits: 0 },
                weekData: [],
                incidents: []
            });
            dispatch(action.retrieve({ memberId: member?.id, markingPeriodId: currentMarkingPeriodId }));
        } else {
            dispatch(getMarkingPeriods({ id: academicYear.id }));
        }
    }, [dispatch, member?.id, member?.person?.type, currentMarkingPeriodId]);

    useEffect(() => {
        if (disciplineItems && disciplineItems.length > 0) {
            let merits = 0;
            let demerits = 0;

            disciplineItems.forEach(item => {
                const points = item.incidentItem?.point || 0;
                if (points > 0) {
                    merits += points;
                } else {
                    demerits += Math.abs(points);
                }
            });

            const totalPoints = merits - demerits;

            const now = new Date();
            const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
            const threeWeeksAgo = new Date(now.getTime() - 21 * 24 * 60 * 60 * 1000);
            const fourWeeksAgo = new Date(now.getTime() - 28 * 24 * 60 * 60 * 1000);

            const weekScores = [0, 0, 0, 0];

            disciplineItems.forEach(item => {
                const incidentDate = new Date(item.incidentDate);
                const points = item.incidentItem?.point || 0;

                if (incidentDate >= oneWeekAgo) {
                    weekScores[0] += points;
                } else if (incidentDate >= twoWeeksAgo) {
                    weekScores[1] += points;
                } else if (incidentDate >= threeWeeksAgo) {
                    weekScores[2] += points;
                } else if (incidentDate >= fourWeeksAgo) {
                    weekScores[3] += points;
                }
            });

            const formatDate = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

            const monthData = [
                { label: 'This Week', score: weekScores[0] },
                { label: 'Last Week', score: weekScores[1] },
                { label: formatDate(threeWeeksAgo), score: weekScores[2] },
                { label: formatDate(fourWeeksAgo), score: weekScores[3] },
            ];

            const incidents = disciplineItems
                .slice()
                .sort((a, b) => new Date(b.incidentDate) - new Date(a.incidentDate))
                .map(item => ({
                    date: new Date(item.incidentDate).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                    }),
                    title: item.incidentItem?.name || 'Unknown Incident',
                    description: item.explanation || '',
                    staff: item.staff ? `${item.staff.lastName}, ${item.staff.firstName}` : 'Unknown',
                    points: item.incidentItem?.point || 0,
                }));

            setData({
                stats: { totalPoints, merits, demerits },
                monthData,
                incidents
            });
        }
    }, [disciplineItems, member.id, currentMarkingPeriodId]);

    if (disciplineLoading) {
        return <LinearProgress />;
    }

    return (
        <Box sx={{ p: 2 }}>
            <DisciplineStats
                stats={data.stats}
                monthData={data.monthData}
                incidents={data.incidents}
            />
        </Box>
    );
};

export default ProfileDisciplineStats;